import { useCallback, useState } from "react";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import { Info2Icon, TriangleArrowDownIcon } from "@/shared/icons";
import { ArrowSwitcher } from "@/shared/ui";
import CreateCharacterBtn from "@/domains/createCharacter/components/CreateCharacterBtn";
import CreateCharacterBtnRandom from "@/domains/createCharacter/components/CreateCharacterBtnRandom";
import CreateCharacterVariantSelectForm from "@/domains/createCharacter/components/CreateCharacterVariantSelectForm";

type Props = {
  label: string;
  value: string | number;
  valueDefault?: string | number;
  valueTitle?: string | number | ((value: string | number) => string | number);
  variants: (string | number)[];
  onChange: (value: string | number) => void;
  images?: string;
  showVariantSelectForm?: boolean;
  hint?: string;
  classNameArrowSwitcher?: string;
};

export default function CreateCharacterVariantSelect({
  label,
  value,
  valueTitle,
  valueDefault,
  variants,
  onChange,
  images,
  showVariantSelectForm = true,
  hint,
  classNameArrowSwitcher,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback(() => {
    setIsOpen((state) => !state);
  }, []);

  return (
    <div>
      <div
        className={clsx(
          "flex justify-between items-center h-13.5 pl-5 pr-2 gap-1.5 bg-dark-800",
          isOpen ? "rounded-t-xl" : "rounded-xl",
        )}
      >
        <div className="flex items-center gap-2 font-bold mr-auto text-15">
          {label}
          {hint && (
            <div className="relative group">
              <Info2Icon
                size={16}
                className="w-4 cursor-pointer text-white/50 hover:text-inverted"
              />
              <div
                className={clsx(
                  "absolute -translate-x-[50%] hidden group-hover:block card mt-2 p-2 rounded-[0.5rem] z-2 min-w-[15rem]",
                  "text-12",
                )}
              >
                {hint}
              </div>
            </div>
          )}
        </div>
        <ArrowSwitcher
          className={cn("max-w-[9.75rem]", classNameArrowSwitcher)}
          valueTitle={valueTitle}
          valueDefault={valueDefault}
          value={value}
          variants={variants}
          onChange={onChange}
        />
        <CreateCharacterBtnRandom variants={variants} onClick={onChange} />
        {showVariantSelectForm && (
          <CreateCharacterBtn active={isOpen} onClick={toggleOpen}>
            <TriangleArrowDownIcon size={20} className="-rotate-90" />
          </CreateCharacterBtn>
        )}
      </div>
      {isOpen && (
        <CreateCharacterVariantSelectForm
          images={images}
          variants={variants}
          valueTitle={valueTitle}
          value={value}
          onChange={onChange}
          valueDefault={valueDefault}
        />
      )}
    </div>
  );
}
